import React, { useState, useEffect } from "react";
import Constants from "../components/Constant.js";
import {
  TextField,
  Button,
  Container,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import MainLayout from "../components/MainLayout.js";
import backgroundImage from "../assets/backgroundHome.png";

export default function EditarPrefeitura() {
  const [prefeituras, setPrefeituras] = useState([]);
  const [filteredPrefeituras, setFilteredPrefeituras] = useState([]);
  const [selectedPrefeitura, setSelectedPrefeitura] = useState(null);
  const [searchPrefeitura, setSearchPrefeitura] = useState("");
  const [formData, setFormData] = useState({
    nome: "",
    cnpj: "",
    email: "",
    telefone: "",
    cep: "",
    logradouro: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    porcentagem: "",
  });
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  useEffect(() => {
    const fetchPrefeituras = async () => {
      try {
        const response = await fetch(Constants.API_CONSULTAR_PREFEITURA, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (response.ok) {
          setPrefeituras(data.prefeituras);
          setFilteredPrefeituras(data.prefeituras);
        } else {
          showSnackbar("Erro ao buscar prefeituras.", "error");
        }
      } catch (error) {
        console.error("Erro ao conectar com o servidor:", error);
        showSnackbar("Erro ao conectar com o servidor.", "error");
      }
    };
    fetchPrefeituras();
  }, [token]);

  useEffect(() => {
    if (searchPrefeitura) {
      const filtered = prefeituras.filter((prefeitura) =>
        prefeitura.PRE_NOME.toLowerCase().includes(searchPrefeitura.toLowerCase())
      );
      setFilteredPrefeituras(filtered);
    } else {
      setFilteredPrefeituras(prefeituras);
    }
  }, [searchPrefeitura, prefeituras]);

  useEffect(() => {
    if (selectedPrefeitura) {
      setFormData({
        nome: selectedPrefeitura.PRE_NOME || "",
        cnpj: selectedPrefeitura.PRE_CNPJ || "",
        email: selectedPrefeitura.PRE_EMAIL || "",
        telefone: selectedPrefeitura.PRE_TELEFONE || "",
        cep: selectedPrefeitura.PRE_CEP || "",
        logradouro: selectedPrefeitura.PRE_LOGRADOURO || "",
        numero: selectedPrefeitura.PRE_NUMERO || "",
        bairro: selectedPrefeitura.PRE_BAIRRO || "",
        cidade: selectedPrefeitura.PRE_CIDADE || "",
        estado: selectedPrefeitura.PRE_ESTADO || "",
        porcentagem: selectedPrefeitura.PRE_PORCENTAGEM_DESC || "",
      });
    }
  }, [selectedPrefeitura]);

  const handlePrefeituraChange = (_, newValue) => {
    setSelectedPrefeitura(newValue);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "porcentagem") {
      // Remove tudo que não é número ou vírgula
      newValue = value.replace(/[^\d,]/g, "");
      // Permite apenas uma vírgula
      newValue = newValue.replace(/(,.*),/g, "$1");
      // Limita a dois dígitos após a vírgula
      if (newValue.includes(",")) {
        const [inteiro, decimal] = newValue.split(",");
        if (decimal && decimal.length > 2) {
          newValue = `${inteiro},${decimal.slice(0, 2)}`;
        }
      }
      // Converte para número para validar o range (0-100)
      const numericValue = Number(newValue.replace(",", "."));
      if (numericValue > 100) {
        newValue = "100";
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.porcentagem.trim()) {
      newErrors.porcentagem = "Porcentagem é obrigatória";
    } else {
      const numericValue = Number(formData.porcentagem.replace(",", "."));
      if (isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
        newErrors.porcentagem = "Porcentagem deve ser um número entre 0 e 100";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!selectedPrefeitura) {
      showSnackbar("Selecione uma prefeitura para editar.", "error");
      return;
    }

    if (!validateForm()) {
      showSnackbar("Por favor, corrija os erros no formulário.", "error");
      return;
    }

    try {
      const response = await fetch(`${Constants.API_ATUALIZAR_PREFEITURA}/${selectedPrefeitura.PRE_ID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          PRE_PORCENTAGEM_DESC: formData.porcentagem.replace(",", ".")
        }),
      });

      if (response.ok) {
        showSnackbar("Porcentagem atualizada com sucesso!", "success");
        // Atualiza a lista de prefeituras
        const updatedPrefeituras = prefeituras.map(pref => 
          pref.PRE_ID === selectedPrefeitura.PRE_ID 
            ? { ...pref, PRE_PORCENTAGEM_DESC: formData.porcentagem }
            : pref
        );
        setPrefeituras(updatedPrefeituras);
        setFilteredPrefeituras(updatedPrefeituras);
      } else {
        const data = await response.json();
        showSnackbar(data.message || "Erro ao atualizar porcentagem.", "error");
      }
    } catch (error) {
      console.error("Erro ao conectar com o servidor:", error);
      showSnackbar("Erro ao conectar com o servidor.", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <MainLayout>
      <Box
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backgroundBlendMode: "overlay",
          padding: 2,
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ p: { xs: 2, sm: 4 }, bgcolor: "background.paper", borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}>
              Editar Porcentagem da Prefeitura
            </Typography>

            {/* Filtro de Prefeituras */}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={filteredPrefeituras}
                  getOptionLabel={(option) => option.PRE_NOME}
                  value={selectedPrefeitura}
                  onChange={handlePrefeituraChange}
                  onInputChange={(_, newInputValue) => {
                    setSearchPrefeitura(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecione uma Prefeitura"
                      fullWidth
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  )}
                />
              </Grid>

              {selectedPrefeitura && (
                <>
                  {/* Informações da Prefeitura */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome da Prefeitura"
                      value={formData.nome}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="CNPJ"
                      value={formData.cnpj}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={formData.email}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Telefone"
                      value={formData.telefone}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="CEP"
                      value={formData.cep}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Logradouro"
                      value={formData.logradouro}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Número"
                      value={formData.numero}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Bairro"
                      value={formData.bairro}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cidade"
                      value={formData.cidade}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Estado"
                      value={formData.estado}
                      disabled
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Porcentagem *"
                      name="porcentagem"
                      value={formData.porcentagem ? `${formData.porcentagem.replace(".", ",")}%` : ""}
                      onChange={(e) => {
                        // Remove o % antes de processar
                        const rawValue = e.target.value.replace("%", "");
                        handleInputChange({
                          target: {
                            name: "porcentagem",
                            value: rawValue
                          }
                        });
                      }}
                      error={!!errors.porcentagem}
                      helperText={errors.porcentagem || "Valor entre 0 e 100%"}
                      margin="normal"
                      size="medium"
                      sx={{ "& .MuiInputBase-root": { height: "56px" } }}
                      inputProps={{ maxLength: 7 }}
                    />
                  </Grid>

                  {/* Botão de Editar */}
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        sx={{ height: "56px", width: "200px" }}
                      >
                        Salvar Alterações
                      </Button>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Container>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
} 